import { useEffect, useState } from "react";
import Link from "next/link";
import sal from "sal.js";
import { useTranslation } from "react-i18next";

import { isRtlLanguage } from "@/lib/i18n";

/**
 * Shared presentational shell for the Terms of Use and Privacy Policy pages.
 * Renders a gradient hero (reusing the same .rbt-page-hero look as the FAQ
 * Help Center for cross-page brand consistency), a sticky in-page table of
 * contents on desktop, a horizontally-scrollable jump nav on mobile, and the
 * legal copy itself inside a series of elevated Material-style cards.
 *
 * `sections` is an array of { id, title, navLabel?, body } where `body` is
 * the section's JSX content (paragraphs, lists, tables, etc).
 */
const LegalPageLayout = ({ eyebrow, title, description, lastUpdated, sections }) => {
  const { i18n } = useTranslation("legal");
  // The `legal` namespace is genuinely translated per-language (unlike blog/event content,
  // which deliberately stays in whatever language the author wrote it in) -- see
  // lib/i18n.js's isRtlLanguage() comment for why this local dir is correct here rather than
  // mirroring the whole page's chrome from the document root.
  const contentDir = isRtlLanguage(i18n.language) ? "rtl" : undefined;
  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");

  useEffect(() => {
    sal({ threshold: 0.01, once: true });
  }, []);

  // Scrollspy: keep whichever TOC entry matches the section nearest the top of the
  // viewport highlighted, in both the desktop sidebar and the mobile pill rail.
  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;

    const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-170px 0px -70% 0px", threshold: 0 }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="rbt-page-hero">
        <div className="container">
          <span className="rbt-page-hero__eyebrow">{eyebrow}</span>
          <h1 className="rbt-page-hero__title">{title}</h1>
          <p className="rbt-page-hero__desc">{description}</p>

          <span className="rbt-legal-updated">
            <i className="feather-clock" aria-hidden="true"></i>
            Last updated: {lastUpdated}
          </span>

          <ul className="page-list justify-content-center">
            <li className="rbt-breadcrumb-item">
              <Link href="/" style={{ color: "rgba(255,255,255,0.85)" }}>Home</Link>
            </li>
            <li>
              <div className="icon-right">
                <i className="feather-chevron-right" style={{ color: "rgba(255,255,255,0.65)" }}></i>
              </div>
            </li>
            <li className="rbt-breadcrumb-item active" style={{ color: "#fff" }}>{title}</li>
          </ul>
        </div>
      </div>

      {/* ── Mobile jump nav ─────────────────────────────────────────────── */}
      <div className="container d-lg-none">
        <nav className="rbt-page-jumpnav" aria-label="Jump to section">
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={`rbt-page-jumpnav__pill ${activeId === section.id ? "is-active" : ""}`}
            >
              {section.navLabel ?? section.title}
            </a>
          ))}
        </nav>
      </div>

      {/* ── Body: sticky TOC + content cards ────────────────────────────── */}
      <div className="rbt-legal-body rbt-section-gap">
        <div className="container">
          <div className="rbt-legal-grid">
            <aside className="rbt-legal-toc d-none d-lg-block">
              <div className="rbt-legal-toc__inner">
                <span className="rbt-legal-toc__label">On this page</span>
                <nav aria-label="Table of contents">
                  <ul>
                    {sections.map((section) => (
                      <li key={section.id}>
                        <a
                          href={`#${section.id}`}
                          className={activeId === section.id ? "is-active" : ""}
                        >
                          {section.navLabel ?? section.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>

            <div className="rbt-legal-content" dir={contentDir}>
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="rbt-legal-card">
                  <h2>{section.title}</h2>
                  {section.body}
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LegalPageLayout;
